import type { Module } from '@/types/course';

export const module5: Module = {
  slug: 'modulo-5-estrategia-inversion',
  number: 5,
  title: 'Estrategia de inversión en oro',
  description:
    'Saber qué oro comprar y dónde guardarlo es táctica. La estrategia es decidir cuánto, cuándo y bajo qué reglas. Marco mental para tomar decisiones con criterio.',
  durationMinutes: 80,
  badge: 'estratega',
  lessons: [
    {
      slug: 'porcentaje-cartera-oro',
      number: '5.1',
      title: '¿Qué porcentaje de tu cartera dedicar al oro?',
      durationMinutes: 22,
      videoUrl: null,
      content: `La pregunta "¿cuánto oro debo tener?" no tiene respuesta universal: depende de tu patrimonio total, edad, perfil de riesgo, otras inversiones, situación profesional y país. Pero la literatura financiera y la experiencia institucional convergen en un rango consistente: **entre el 5% y el 15% del patrimonio invertible total** para la mayoría de perfiles.

**¿Por qué ese rango?**

El **5%** es el mínimo para que el oro tenga impacto real. Por debajo, su efecto descorrelacionador es marginal.

El **15%** es el máximo razonable salvo perfiles muy específicos. Por encima, sacrificas demasiada rentabilidad esperada.

**Modelos de referencia**:

- **Permanent Portfolio (Harry Browne)**: 25% bolsa, 25% bonos largos, 25% cash, 25% oro. Drawdowns muy bajos pero rentabilidad inferior.

- **All Weather (Ray Dalio)**: 30% acciones, 40% bonos largos, 15% bonos medios, 7,5% oro, 7,5% otras commodities.

**Recomendaciones por perfil**:

| Perfil | Asignación oro |
|---|---|
| Conservador puro (poca tolerancia al riesgo) | 10-15% |
| Equilibrado | 8-12% |
| Agresivo (>40 años hasta retiro) | 5-8% |
| Joven (>25 años hasta retiro) | 3-7% |
| Patrimonio muy elevado / generacional | 10-20% |
| Empresario/autónomo expuesto a riesgos económicos | 12-18% |

**Cómo calcular tu asignación**:

1. **Patrimonio invertible total** (excluyendo vivienda habitual y fondo de emergencia):
   - Activos líquidos: cuentas, depósitos, bolsa, fondos.
   - Activos tangibles invertibles: oro, otras commodities, inmuebles de inversión.
   - Otros: cripto, capital invertido en negocio.

2. **Aplica el porcentaje** correspondiente a tu perfil.

3. **Compara con tu posición actual** y ajusta gradualmente.

**Ejemplo**: María, 45 años, perfil equilibrado, patrimonio invertible 250.000€. Asignación recomendada: 10%. Objetivo: 25.000€ en oro físico.

**Si tu patrimonio crece, ¿mantienes el porcentaje?**

Sí. La asignación es un **ratio de cartera**, no un valor absoluto. Si tu patrimonio crece de 250.000€ a 500.000€, tu objetivo de oro pasa de 25.000€ a 50.000€.

**Si el oro sube y se sobrepasa el porcentaje**:

- **Rebalanceo estricto**: vender oro hasta volver al objetivo.
- **Rebalanceo blando**: dejar correr ganadores, ajustar solo en desviaciones grandes (+5 puntos).

Para inversores patrimoniales pacientes, el rebalanceo blando con ajustes cada 2-3 años suele ser óptimo.

> *Disclaimer: Estas asignaciones son orientativas. Tu asignación óptima depende de tu situación particular y debería revisarse con un asesor cualificado.*`,
      keyPoints: [
        'Asignación al oro: 5-15% del patrimonio invertible para la mayoría de perfiles.',
        'Modelos de referencia: Permanent Portfolio (25%) y All Weather (7,5%).',
        'Perfiles conservadores y patrimonios elevados tienden al 10-15%; perfiles agresivos o jóvenes al 5-8%.',
        'La asignación es un ratio: ajusta el oro al crecimiento de tu patrimonio total.',
        'Rebalanceo periódico mantiene la disciplina de la asignación objetivo.',
      ],
    },
    {
      slug: 'dca-oro',
      number: '5.2',
      title: 'DCA aplicado al oro',
      durationMinutes: 18,
      videoUrl: null,
      content: `El **Dollar Cost Averaging (DCA)**, "promediación del coste", es la estrategia más recomendada por la literatura académica para inversores no profesionales. Su lógica es simple pero su efecto en el comportamiento del inversor es transformador.

**Definición**: en lugar de invertir una cantidad grande de golpe (lump sum), divides la inversión en compras periódicas de cantidad fija. Cuando el precio está alto, compras menos unidades; cuando está bajo, compras más. El resultado es un **precio medio de adquisición más favorable**.

**Ejemplo**: 24.000€ para invertir en oro. Tres opciones:

**A) Lump sum**: todo en enero a 73€/g = 328,8 g.

**B) DCA mensual**: 2.000€/mes durante 12 meses. Precios entre 70€/g y 80€/g. Final: 320 g a precio medio 75€/g.

**C) Timing manual**: intentas "suelos". Compras en marzo a 71€, en julio a 76€ (creías suelo y subió), en noviembre a 78€. Total: 313 g a precio medio 76,7€/g.

**Académicamente**, el lump sum gana ligeramente en mercados alcistas a largo plazo (~67% del tiempo según estudios). Pero el DCA **gana sistemáticamente en estabilidad emocional y disciplina**.

**Por qué el DCA funciona en oro**:
1. **Volatilidad a corto plazo**: oscilaciones del 5-10% en pocas semanas.
2. **Timing perfecto imposible** para inversores no profesionales.
3. **Disciplina conductual**: la inversión se convierte en hábito.
4. **Promedia las correcciones**.

**DCA aplicado al oro físico** tiene un problema: las primas son inversamente proporcionales al tamaño. Comprar 200€/mes en lingotes de 1-2g paga primas del 8-12%, lo cual erosiona el beneficio.

**Estrategias prácticas**:

**Opción 1 — DCA trimestral**:
- 6.000€ por trimestre.
- Lingotes de 50-100 g.
- 4 compras al año, primas controladas (3-5%).
- Patrimonios objetivo >24.000€/año.

**Opción 2 — DCA semestral**:
- 12.000€ cada 6 meses.
- Lingotes de 250 g o 500 g (primas 1,5-2,5%).
- 2 compras al año, máxima eficiencia.

**Opción 3 — DCA anual**:
- Una compra al año de un lingote de 1 kg.
- Prima mínima (1-2%).

**Opción 4 — DCA híbrido**:
- DCA mensual en ETF de oro (Xetra-Gold) por cantidades pequeñas.
- Conversión periódica a oro físico cuando alcanza umbrales.

**Cuándo el lump sum es preferible**:
- Capital extraordinario (herencia, venta) y fundamentales favorables.
- Horizonte muy largo (15+ años).
- Si la prima por DCA fragmentado es muy alta.

**Recomendación intermedia para patrimonios significativos**: 50% lump sum + 50% DCA semestral durante 18-24 meses.`,
      keyPoints: [
        'DCA = comprar cantidades fijas a intervalos regulares; promedia el coste y reduce riesgo de timing.',
        'En oro físico, DCA mensual de pequeñas cantidades es ineficiente (primas altas).',
        'DCA trimestral o semestral en lingotes medios/grandes equilibra disciplina y eficiencia.',
        'Para capitales grandes, considerar 50% lump sum + 50% DCA semestral.',
        'El verdadero valor del DCA es conductual: convierte la inversión en hábito.',
      ],
    },
    {
      slug: 'cuando-comprar-vender',
      number: '5.3',
      title: 'Cuándo comprar y cuándo (no) vender',
      durationMinutes: 20,
      videoUrl: null,
      content: `El oro no es para tradear. Pero eso no significa que el momento sea irrelevante.

**Cuándo comprar**:

**1. Tipos reales bajos o negativos**. El **coste de oportunidad** del oro es menor cuando los bonos pagan poco o pierden frente a la inflación. Tipos reales = nominal − inflación esperada. Por debajo del 1%, el oro tiende a comportarse bien.

**2. Dólar débil**. El oro cotiza en dólares globalmente. Dólar débil → oro más barato para compradores en otras monedas → más demanda y precio. Indicador: índice DXY <100.

**3. Inflación elevada**. Cuando supera el 3-4% sostenido, el oro recupera su narrativa.

**4. Incertidumbre geopolítica**. Tensiones serias suelen impulsar el oro.

**5. Crisis bancarias o sistémicas**. Lehman 2008, SVB 2023: el oro se beneficia como activo sin contraparte.

**6. Correcciones técnicas en tendencia alcista**. Caídas del 5-10% sobre máximos suelen ser entradas razonables.

**Cuándo NO comprar (o con menos prisa)**:

**1. Tipos reales muy positivos (>3%)**: bonos pagan claramente por encima de la inflación.

**2. Dólar fuerte y subiendo**.

**3. Después de subidas verticales**: cuando el oro sube 30-40% en pocos meses, suele venir corrección.

**4. En picos de pánico de mercado**: las primas físicas se disparan, suele coincidir con techos a corto plazo.

**Cuándo vender (los pocos casos legítimos)**:

**1. Necesidad real de liquidez** para oportunidad superior o emergencia grave.

**2. Rebalanceo de cartera**: si el oro pasó del 10% al 25% de tu patrimonio.

**3. Final de un megaciclo de oro**: tras x4-x5 en una década (raro, pero posible). Señales: euforia mediática, primas anormales, recomendaciones masivas.

**4. Cambio estructural personal**: jubilación con necesidad de rentas, mudanza internacional, salud, etc.

**Cuándo NO vender (errores comunes)**:

**1. Tras corrección del 10-15%**: vender en pánico es el error más caro.

**2. Porque "ha subido mucho"**: si compraste para 15 años y va por el 3 con +40%, vender traiciona la estrategia.

**3. Por ruido mediático bajista**: las narrativas anti-oro se repiten cada pocos años.

**4. Por aburrimiento**: el oro recompensa al paciente.

**Regla práctica**:

> **Compras programadas, ventas pensadas.** Las compras pueden seguir reglas casi automáticas (DCA, rebalanceos). Las ventas deben requerir razonamiento explícito.

> *Este contenido es educativo y no constituye asesoramiento financiero personalizado.*`,
      keyPoints: [
        'Buenos momentos: tipos reales bajos, dólar débil, inflación alta, incertidumbre, crisis bancarias, correcciones.',
        'Malos momentos: tipos reales muy positivos, dólar fuerte, tras subidas verticales, en picos de pánico.',
        'Vender es legítimo solo por: necesidad real, rebalanceo, fin de megaciclo, cambio estructural.',
        'Errores caros: vender en correcciones, vender por subida sin cumplir horizonte, vender por ruido mediático.',
        'Regla: compras programadas, ventas pensadas y justificadas.',
      ],
    },
    {
      slug: 'oro-crisis-casos-reales',
      number: '5.4',
      title: 'El oro en momentos de crisis: casos reales',
      durationMinutes: 20,
      videoUrl: null,
      content: `La teoría de "oro como refugio" se sostiene o cae con datos. Examinemos tres crisis recientes.

**Caso 1: Crisis Financiera Global (2007-2011)**

**Contexto**: colapso subprime, quiebra de Lehman (sept. 2008), rescates bancarios, expansión cuantitativa (QE) sin precedentes.

**Comportamiento del oro**:
- 2007 precrisis: 650-840 USD/oz.
- Sept-Oct 2008 (pánico Lehman): cae temporalmente de 920 a 720 USD/oz. **Razón**: en pánicos puntuales, los inversores venden todo activo líquido —incluido oro— para cubrir posiciones apalancadas. El oro **inicialmente cae** pero rápidamente recupera.
- Nov 2008 - Sept 2011: pasa de 720 a **1.920 USD/oz**. Subida del 167% en menos de 3 años.
- 2012-2015: corrección hasta 1.050 USD/oz.

**Lección**: el oro tuvo el comportamiento clásico del activo refugio en una crisis financiera sistémica. La caída inicial fue ruido; la subida estructural confirmó la tesis.

**Caso 2: Crisis del COVID-19 (2020)**

**Contexto**: pandemia, confinamientos, parón económico global, intervenciones fiscales y monetarias masivas.

**Comportamiento del oro**:
- Enero 2020: 1.520 USD/oz.
- Marzo 2020 (pánico inicial): cae temporalmente a 1.470 USD/oz (caída del 3%, comparada con 34% de la bolsa).
- Agosto 2020: alcanza máximo histórico de **2.060 USD/oz**.
- 2020-2021: estabilización en 1.800-1.900 USD/oz.

**Lección**: el oro confirmó su rol descorrelacionador. Las primas físicas se dispararon enormemente en la primera mitad de 2020 (algunas monedas con primas del 15-25%) por demanda de minoristas asustados.

**Caso 3: Crisis Inflacionaria 2022-2023**

**Contexto**: tras estímulos COVID + guerra en Ucrania, inflación global supera 8-10% en muchos países. Bancos centrales suben tipos agresivamente.

**Comportamiento del oro**:
- Inicial: ligero desconcierto. Sube en marzo 2022 con la guerra (>2.000 USD/oz) pero luego cae con la subida de tipos al 4-5%.
- 2022: oscila entre 1.620 y 2.050 USD/oz.
- 2023-2024: nueva tendencia alcista alcanzando **>2.500 USD/oz** en agosto 2024.

**Lección**: el oro no responde linealmente a la inflación cuando los tipos suben más rápido que la inflación misma (tipos reales positivos). Pero cuando los tipos reales se moderan, el oro retoma su narrativa. **El factor crítico no es la inflación nominal, sino los tipos reales**.

**Patrones recurrentes**:

1. **Caída inicial en pánico**: en las primeras semanas de una crisis, el oro puede caer junto con todo. No te asustes.

2. **Subida estructural posterior**: cuando se materializa la respuesta de los bancos centrales (estímulos, QE, bajadas de tipos), el oro sube fuertemente.

3. **Primas físicas se disparan en pánicos retail**: cuando ciudadanos se asustan y todos quieren oro, las primas se inflan. Comprar en ese momento es subóptimo.

4. **El oro responde a tipos reales más que a la inflación nominal**: alta inflación con tipos altos puede no ser bullish para el oro; baja inflación con tipos negativos sí lo es.

5. **Los bancos centrales son el comprador estructural**: en todas estas crisis, los bancos centrales aceleraron sus compras de oro. Es señal de tendencia macro.

> *Este contenido es educativo y se basa en datos históricos. Rentabilidades pasadas no garantizan rentabilidades futuras.*`,
      keyPoints: [
        'En 2008, el oro cayó inicialmente con el pánico pero subió 167% en los 3 años siguientes.',
        'En 2020, el oro alcanzó máximo histórico de 2.060 USD/oz mientras la bolsa caía 34%.',
        'En 2022-2023, el oro respondió a tipos reales más que a la inflación nominal pura.',
        'Patrones: caída inicial en pánico, subida estructural posterior, primas retail disparadas, respuesta a tipos reales.',
        'Los bancos centrales son compradores estructurales en crisis: señal de tendencia macro.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm5-q1',
      question:
        'Según la literatura financiera y gestores como Ray Dalio, ¿cuál es la asignación recomendada al oro en una cartera diversificada?',
      options: ['0-2%', '5-15%', '25-40%', '50% o más'],
      correctIndex: 1,
      explanation:
        'La asignación recomendada para la mayoría de perfiles está entre 5% y 15% del patrimonio invertible. El All Weather de Dalio incluye 7,5%.',
    },
    {
      id: 'm5-q2',
      question: '¿Cuál es la principal ventaja del DCA (Dollar Cost Averaging)?',
      options: [
        'Garantiza mayor rentabilidad que el lump sum',
        'Elimina el riesgo de pérdidas',
        'Promedia el precio de compra y elimina el problema de acertar el timing',
        'Reduce los impuestos pagados',
      ],
      correctIndex: 2,
      explanation:
        'El DCA promedia el coste de adquisición y aporta disciplina conductual. Académicamente, el lump sum gana ligeramente en mercados alcistas, pero el DCA gana en estabilidad emocional y disciplina.',
    },
    {
      id: 'm5-q3',
      question: '¿En qué momento NO es razonable vender oro?',
      options: [
        'Necesitas liquidez para una oportunidad de inversión claramente superior',
        'Has alcanzado tu objetivo de horizonte temporal',
        'El oro ha caído un 10-15% sobre máximos recientes y tienes miedo',
        'Quieres rebalancear porque ha pasado del 10% al 25% de tu cartera',
      ],
      correctIndex: 2,
      explanation:
        'Vender en correcciones por miedo es el error más común y caro. Las correcciones del 5-15% son normales en el oro y rara vez son finales de tendencia.',
    },
    {
      id: 'm5-q4',
      question:
        'Durante la crisis financiera de 2008, ¿qué hizo el oro inicialmente al estallar el pánico?',
      options: [
        'Subió inmediatamente sin parar',
        'Se mantuvo plano',
        'Cayó temporalmente con el pánico, pero luego subió un 167% en 3 años',
        'Se volvió ilegal',
      ],
      correctIndex: 2,
      explanation:
        'En pánicos iniciales, los inversores venden incluso oro para cubrir posiciones apalancadas. Pero la intervención posterior de bancos centrales con estímulos suele desencadenar subidas estructurales.',
    },
    {
      id: 'm5-q5',
      question: '¿Qué factor influye MÁS en el precio del oro a medio plazo?',
      options: [
        'El precio del petróleo',
        'Los tipos de interés reales (nominal − inflación esperada)',
        'La cantidad de oro extraído cada año',
        'Las decisiones de la OPEP',
      ],
      correctIndex: 1,
      explanation:
        'El oro responde principalmente a tipos reales: cuando son negativos o muy bajos, el oro brilla. Cuando son muy positivos, tiene vientos en contra. Esto explica por qué a veces el oro sube con inflación y otras no.',
    },
  ],
};
